import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { DatesService } from '../../../services/dates.service';
import { CURRENT_TRIP_MEMBERS, membersFilter } from '@at/trip/people';
import { DisplayNameComponent } from '@at/user';
import { Rsvp, RsvpStatus, RsvpStatuses } from '../../../entities';
import { GroupedMemberMap, IsTripOwnerPipe, MemberMap, MembersFilterComponent } from '@at/trip';
import { NgTemplateOutlet } from '@angular/common';

@Component({
  selector: 'at-rsvp-responses',
  standalone: true,
  imports: [MembersFilterComponent, NgTemplateOutlet, DisplayNameComponent, IsTripOwnerPipe],
  templateUrl: './rsvp-responses.component.html',
  styleUrl: './rsvp-responses.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RsvpResponsesComponent {
  #datesService = inject(DatesService);
  #tripParticipants = inject(CURRENT_TRIP_MEMBERS);

  protected readonly state = this.#datesService.datesSummary;
  protected readonly startDate = computed(() => {
    const startDate = this.state()?.data?.rangeCriteria?.start;
    if (!startDate) return new Date();

    return startDate;
  });
  protected readonly endDate = computed(() => {
    const endDate = this.state()?.data?.rangeCriteria?.end;
    if (!endDate) return new Date();

    return endDate;
  });

  protected readonly participantsResponses = computed<MemberMap<Rsvp>[]>(() => {
    const responses = this.state()?.data?.groupResponses?.rsvpResponses;
    const participants = this.#tripParticipants();

    if (!responses || !participants) return [];

    return participants.map((participant) => {
      const response = responses.find((r) => r.userId === participant.userId);

      return response
        ? {
            ...response,
            ...participant,
          }
        : { status: RsvpStatuses.NotReplied, ...participant };
    });
  });

  protected readonly filterValue = signal<string | undefined>(undefined);
  protected readonly isFilterResultEmpty = computed(() => {
    const responses = this.filteredResponses();
    return (
      responses.Maybe.length === 0 &&
      responses.Yes.length === 0 &&
      responses.No.length === 0 &&
      responses.NotReplied.length === 0
    );
  });

  protected readonly filteredResponses = computed(() => {
    const filter = this.filterValue();
    const responses = this.participantsResponses();
    if (!filter) return this.#getMembersMap(responses);

    return this.#getMembersMap(membersFilter(responses, filter));
  });

  #getMembersMap(members: MemberMap<Rsvp>[]): GroupedMemberMap<RsvpStatus, Rsvp> {
    return members.reduce(
      (acc: GroupedMemberMap<RsvpStatus, Rsvp>, participant) => {
        const group = acc[participant.status];
        acc[participant.status] = [...group, participant];

        return acc;
      },
      {
        Maybe: [],
        Yes: [],
        No: [],
        NotReplied: [],
      },
    );
  }
}
